import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ListVideo, Plus, Trash2, UploadCloud, Loader2, Play, X, ArrowLeft, Film, Radio, Clock, Code2, Copy, Check, RefreshCw, Star, Download, ChevronUp, ChevronDown, ArrowDownUp } from "lucide-react";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { buildDownloadUrl, safeVideoFilename, canDownload, itemDownloadUrl } from "@/lib/download";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import HlsPlayer from "@/components/HlsPlayer";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const CHUNK = 5 * 1024 * 1024; // 5MB
const fmtDur = (s) => { const m = Math.floor(s / 60), sec = s % 60; return s ? `${m}m ${sec}s` : "—"; };

const StatusPill = ({ status }) => {
  const map = { ready: ["Ready", "text-emerald-400 bg-emerald-500/10 border-emerald-500/30"], processing: ["Compressing…", "text-amber-400 bg-amber-500/10 border-amber-500/30"], uploading: ["Uploading…", "text-blue-400 bg-blue-500/10 border-blue-500/30"], failed: ["Failed", "text-red-400 bg-red-500/10 border-red-500/30"] };
  const [label, cls] = map[status] || map.ready;
  return <span className={"text-[10px] font-semibold px-1.5 py-0.5 rounded border " + cls}>{label}</span>;
};

export default function Playlists() {
  const { user } = useAuth();
  const [playlists, setPlaylists] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [recordings, setRecordings] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [hideBar, setHideBar] = useState(false);
  const [customColors, setCustomColors] = useState(false);
  const [barColor, setBarColor] = useState("#3B82F6");
  const [barTextColor, setBarTextColor] = useState("#FFFFFF");
  const [upload, setUpload] = useState(null); // {name, pct}
  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState("");
  const fileRef = useRef(null);

  const loadPlaylists = async () => {
    try { const r = await api.get("/playlists"); setPlaylists(r.data); } finally { setLoading(false); }
  };

  const toggleDefault = async (pl) => {
    try {
      if (pl.is_default) {
        await api.post("/playlists/clear-default");
        toast.success("Default playlist cleared — new videos won't auto-add");
      } else {
        await api.post(`/playlists/${pl.id}/set-default`);
        toast.success(`New uploads & recordings will auto-add to "${pl.name}"`);
      }
      loadPlaylists();
    } catch { toast.error("Couldn't update default playlist"); }
  };
  useEffect(() => { loadPlaylists(); }, [user?.role]);

  useEffect(() => {
    api.get("/upload-profiles").then((r) => {
      setProfiles(r.data);
      const def = r.data.find((p) => p.is_default) || r.data[0];
      if (def) setProfileId(def.id);
    }).catch(() => {});
  }, []);

  const openPlaylist = async (id) => {
    const r = await api.get(`/playlists/${id}`);
    setActive(r.data);
    // auto-refresh while any item is processing
  };

  // Remember each playlist's preferred upload quality.
  useEffect(() => {
    if (!active) return;
    if (active.default_profile_id) { setProfileId(active.default_profile_id); return; }
    const def = profiles.find((p) => p.is_default) || profiles[0];
    if (def) setProfileId(def.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id, profiles]);

  const onChangeProfile = async (id) => {
    setProfileId(id);
    if (!active) return;
    try {
      await api.put(`/playlists/${active.id}`, { default_profile_id: id });
      setActive((a) => (a ? { ...a, default_profile_id: id } : a));
      const p = profiles.find((x) => x.id === id);
      if (p) toast.success(`Upload quality set to ${p.height}p for this playlist`);
    } catch { toast.error("Couldn't save quality preference"); }
  };

  useEffect(() => {
    if (!active) return;
    const hasProcessing = active.resolved_items?.some((i) => i.status === "processing" || i.status === "uploading");
    if (!hasProcessing) return;
    const t = setInterval(() => openPlaylist(active.id), 5000);
    return () => clearInterval(t);
  }, [active]);

  const create = async () => {
    if (!newName.trim()) return;
    const r = await api.post("/playlists", { name: newName.trim() });
    setPlaylists((p) => [r.data, ...p]);
    setNewName(""); setCreating(false);
    toast.success("Playlist created");
  };

  const removePlaylist = async (id) => {
    await api.delete(`/playlists/${id}`);
    setPlaylists((p) => p.filter((x) => x.id !== id));
    if (active?.id === id) setActive(null);
    toast.success("Playlist deleted");
  };

  const openAdd = async () => {
    const inPlaylist = new Set((active?.items || []).map((it) => `${it.kind}:${it.id}`));
    const [recs, ups] = await Promise.all([api.get("/recordings"), api.get("/uploads")]);
    setRecordings(recs.data.filter((x) => x.playback_url && !inPlaylist.has(`recording:${x.id}`)));
    setUploads(ups.data.filter((x) => x.playback_url && x.status === "ready" && !inPlaylist.has(`upload:${x.id}`)));
    setAddOpen(true);
  };
  const addItem = async (kind, item) => {
    const r = await api.post(`/playlists/${active.id}/items`, { kind, id: item.id });
    setActive(r.data);
    if (kind === "recording") setRecordings((l) => l.filter((x) => x.id !== item.id));
    else setUploads((l) => l.filter((x) => x.id !== item.id));
    toast.success("Added to playlist");
  };
  const removeItem = async (item) => {
    const r = await api.delete(`/playlists/${active.id}/items/${item.kind}/${item.id}`);
    setActive(r.data);
  };
  const move = async (idx, dir) => {
    const resolved = active.resolved_items || [];
    const j = idx + dir;
    if (j < 0 || j >= resolved.length) return;
    const order = resolved.map((it) => ({ kind: it.kind, id: it.id }));
    [order[idx], order[j]] = [order[j], order[idx]];
    const keys = new Set(resolved.map((it) => `${it.kind}:${it.id}`));
    const orphans = (active.items || []).filter((it) => !keys.has(`${it.kind}:${it.id}`));
    try {
      const r = await api.put(`/playlists/${active.id}`, { items: [...order, ...orphans] });
      setActive(r.data);
    } catch { toast.error("Couldn't reorder"); }
  };
  const changeSort = async (mode) => {
    try {
      const r = await api.put(`/playlists/${active.id}`, { sort_mode: mode });
      setActive(r.data);
      toast.success(mode === "custom" ? "Custom order kept" : `Sorted ${mode === "newest" ? "newest" : "oldest"} first`);
    } catch { toast.error("Couldn't change sort"); }
  };

  const onPickFile = () => fileRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 2.05 * 1024 * 1024 * 1024) { toast.error("File exceeds 2 GB"); return; }
    try {
      setUpload({ name: file.name, pct: 0 });
      const init = await api.post("/uploads/init", { title: file.name.replace(/\.[^.]+$/, ""), filename: file.name, size: file.size, playlist_id: active.id, profile_id: profileId || undefined });
      const uid = init.data.upload_id;
      let sent = 0;
      for (let start = 0; start < file.size; start += CHUNK) {
        const blob = file.slice(start, start + CHUNK);
        await api.put(`/uploads/${uid}/chunk`, blob, { headers: { "Content-Type": "application/octet-stream" } });
        sent += blob.size;
        setUpload({ name: file.name, pct: Math.round((sent / file.size) * 100) });
      }
      await api.post(`/uploads/${uid}/complete`);
      setUpload(null);
      const prof = profiles.find((p) => p.id === profileId);
      toast.success(`Uploaded — compressing${prof ? ` to ${prof.height}p` : ""} now`);
      openPlaylist(active.id);
    } catch (err) {
      setUpload(null);
      toast.error("Upload failed");
    }
  };

  const embedParams = [
    `playlist=${active?.id}`,
    hideBar ? "bar=0" : "",
    (!hideBar && customColors) ? `accent=${barColor.replace("#", "")}&text=${barTextColor.replace("#", "")}` : "",
  ].filter(Boolean).join("&");
  const embedSrc = active ? `${window.location.origin}/embed/library/${user?.user_id}?${embedParams}` : "";
  const embedCode = `<iframe src="${embedSrc}" width="100%" height="600" frameborder="0" allow="autoplay; fullscreen; encrypted-media" allowfullscreen style="border:0;border-radius:12px;"></iframe>`;
  const copyEmbed = async () => { try { await navigator.clipboard.writeText(embedCode); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch { toast.error("Copy failed"); } };

  if (loading) return <div className="flex items-center justify-center py-40"><Loader2 className="h-6 w-6 text-amber-400 animate-spin" /></div>;

  // ---------- Playlist detail ----------
  if (active) {
    const items = active.resolved_items || [];
    const downloadable = items.filter((it) => it.status === "ready" && canDownload(it));
    const downloadableCount = downloadable.length;
    const downloadAll = () => {
      if (!downloadableCount) { toast.error("No downloadable videos in this playlist"); return; }
      toast.success(`Starting ${downloadableCount} download${downloadableCount === 1 ? "" : "s"} — your browser may ask to allow multiple files`);
      downloadable.forEach((it, i) => {
        setTimeout(() => {
          const a = document.createElement("a");
          a.href = itemDownloadUrl(it, API);
          a.download = safeVideoFilename(it.title);
          document.body.appendChild(a);
          a.click();
          a.remove();
        }, i * 1200);
      });
    };
    return (
      <div className="p-6 lg:p-8 max-w-[1100px] mx-auto">
        <button data-testid="back-to-playlists" onClick={() => setActive(null)} className="flex items-center gap-2 text-sm text-gray-400 hover:text-amber-400 mb-5"><ArrowLeft className="h-4 w-4" /> All playlists</button>
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">{active.name}</h1>
            <p className="text-gray-400 mt-1 text-sm">{items.length} video{items.length === 1 ? "" : "s"}</p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            {profiles.length > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500 hidden sm:inline">Quality</span>
                <select data-testid="upload-profile-select" value={profileId} onChange={(e) => onChangeProfile(e.target.value)}
                  className="h-11 rounded-lg bg-gray-950 border border-gray-800 text-sm text-gray-200 px-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/40 cursor-pointer">
                  {profiles.map((p) => <option key={p.id} value={p.id} className="bg-gray-950">{p.name} ({p.height}p){p.is_default ? " • default" : ""}</option>)}
                </select>
              </div>
            )}
            <button data-testid="upload-video-btn" onClick={onPickFile} className="gold-gradient text-black font-semibold px-4 py-2.5 rounded-lg flex items-center gap-2 hover:brightness-110"><UploadCloud className="h-4 w-4" /> Upload video</button>
            <button data-testid="add-recording-btn" onClick={openAdd} className="border border-gray-700 text-gray-100 px-4 py-2.5 rounded-lg flex items-center gap-2 hover:border-amber-500/50"><Plus className="h-4 w-4" /> Add video</button>
            {downloadableCount > 0 && (
              <button data-testid="download-all-btn" onClick={downloadAll} className="border border-amber-500/40 text-amber-300 px-4 py-2.5 rounded-lg flex items-center gap-2 hover:bg-amber-500/10 transition-colors"><Download className="h-4 w-4" /> Download all ({downloadableCount})</button>
            )}
            <button data-testid="playlist-embed-btn" onClick={() => setEmbedOpen(true)} className="border border-gray-700 text-gray-100 px-4 py-2.5 rounded-lg flex items-center gap-2 hover:border-amber-500/50"><Code2 className="h-4 w-4" /> Embed</button>
          </div>
          <input ref={fileRef} type="file" accept="video/*" className="hidden" data-testid="upload-file-input" onChange={onFile} />
        </div>

        {upload && (
          <div data-testid="upload-progress" className="mb-5 rounded-xl border border-blue-500/25 bg-blue-500/[0.06] p-4">
            <p className="text-sm text-blue-200 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Uploading {upload.name} — {upload.pct}%</p>
            <div className="mt-2 h-1.5 bg-gray-800 rounded-full overflow-hidden"><div className="h-full gold-gradient" style={{ width: `${upload.pct}%` }} /></div>
          </div>
        )}

        {items.length > 1 && (
          <div className="flex items-center justify-between gap-3 mb-3" data-testid="playlist-sort-bar">
            <p className="text-xs text-gray-500">{active.sort_mode === "custom" ? "Custom order — use the arrows to move videos. New broadcasts are added at the top." : "Use the arrows to move videos; that switches this playlist to Custom order."}</p>
            <label className="flex items-center gap-2 text-xs text-gray-400 shrink-0">
              <ArrowDownUp className="h-3.5 w-3.5" /> Sort
              <select data-testid="playlist-sort-select" value={active.sort_mode || "newest"} onChange={(e) => changeSort(e.target.value)}
                className="h-9 rounded-lg bg-gray-950 border border-gray-800 text-sm text-gray-200 px-2.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/40 cursor-pointer">
                <option value="newest" className="bg-gray-950">Newest first</option>
                <option value="oldest" className="bg-gray-950">Oldest first</option>
                <option value="custom" className="bg-gray-950">Custom</option>
              </select>
            </label>
          </div>
        )}

        {items.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-gray-800 rounded-2xl">
            <ListVideo className="h-8 w-8 text-amber-400 mx-auto" />
            <p className="mt-3 text-white font-medium">This playlist is empty</p>
            <p className="text-gray-500 text-sm mt-1">Upload a video or add one of your saved broadcasts.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((it, idx) => (
              <div key={it.kind + it.id} data-testid={`playlist-item-${it.id}`} className="flex items-center gap-3 bg-card border border-gray-800 rounded-xl p-3">
                <div className="flex flex-col">
                  <button data-testid={`move-up-${it.id}`} title="Move up" onClick={() => move(idx, -1)} disabled={idx === 0} className="h-6 w-7 rounded-md flex items-center justify-center text-gray-300 hover:text-amber-400 hover:bg-gray-800 disabled:opacity-25 disabled:hover:bg-transparent transition-colors"><ChevronUp className="h-5 w-5" /></button>
                  <button data-testid={`move-down-${it.id}`} title="Move down" onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="h-6 w-7 rounded-md flex items-center justify-center text-gray-300 hover:text-amber-400 hover:bg-gray-800 disabled:opacity-25 disabled:hover:bg-transparent transition-colors"><ChevronDown className="h-5 w-5" /></button>
                </div>
                <button onClick={() => it.playback_url && it.status === "ready" && setPlaying(it)} className="relative h-14 w-24 shrink-0 rounded-md overflow-hidden bg-black flex items-center justify-center">
                  {it.thumbnail_url ? <img src={it.thumbnail_url} alt="" className="h-full w-full object-cover" /> : <Play className="h-4 w-4 text-gray-600" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{it.title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <StatusPill status={it.status} />
                    <span className="text-[11px] text-gray-500 inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDur(it.duration_seconds)}</span>
                    <span className="text-[11px] text-gray-600 inline-flex items-center gap-1">{it.kind === "upload" ? <><UploadCloud className="h-3 w-3" /> Upload</> : <><Radio className="h-3 w-3" /> Broadcast</>}</span>
                  </div>
                </div>
                {it.status === "ready" && it.playback_url && (
                  <button data-testid={`play-item-${it.id}`} onClick={() => setPlaying(it)} className="h-9 w-9 rounded-lg gold-gradient text-black flex items-center justify-center"><Play className="h-4 w-4" fill="currentColor" /></button>
                )}
                {it.status === "ready" && canDownload(it) && (
                  <a
                    data-testid={`download-item-${it.id}`}
                    href={itemDownloadUrl(it, API)}
                    download={safeVideoFilename(it.title)}
                    title="Download video"
                    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg gold-gradient text-black text-xs font-semibold hover:brightness-110 transition-all active:scale-[0.98]"
                  >
                    <Download className="h-4 w-4" /> Download
                  </a>
                )}
                <button data-testid={`remove-item-${it.id}`} onClick={() => removeItem(it)} className="h-9 w-9 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 flex items-center justify-center"><X className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}

        {/* Play modal */}
        <Dialog open={!!playing} onOpenChange={(o) => !o && setPlaying(null)}>
          <DialogContent className="bg-card border-gray-800 max-w-3xl p-0 overflow-hidden">
            <DialogHeader className="px-5 pt-5"><DialogTitle className="font-display text-lg">{playing?.title}</DialogTitle></DialogHeader>
            <div className="aspect-video bg-black mt-3">{playing && <HlsPlayer key={playing.id} src={playing.playback_url} poster={playing.thumbnail_url} autoPlay controls />}</div>
          </DialogContent>
        </Dialog>

        {/* Add video modal (uploads + recordings) */}
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent className="bg-card border-gray-800 max-w-lg">
            <DialogHeader><DialogTitle className="font-display text-lg">Add a video</DialogTitle></DialogHeader>
            <div className="max-h-80 overflow-y-auto space-y-2">
              {uploads.length === 0 && recordings.length === 0 ? (
                <p className="text-sm text-gray-500 py-6 text-center">No videos available to add. Upload a video or record a broadcast first — anything already in this playlist is hidden.</p>
              ) : (
                <>
                  {uploads.map((u) => (
                    <button key={`u-${u.id}`} data-testid={`add-upload-${u.id}`} onClick={() => addItem("upload", u)} className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 text-left">
                      <div className="h-12 w-20 rounded bg-black overflow-hidden flex items-center justify-center shrink-0">{u.thumbnail_url ? <img src={u.thumbnail_url} className="h-full w-full object-cover" alt="" /> : <UploadCloud className="h-4 w-4 text-gray-600" />}</div>
                      <span className="text-sm text-gray-200 truncate flex-1">{u.title}</span>
                      <span className="text-[10px] text-gray-500 inline-flex items-center gap-1"><UploadCloud className="h-3 w-3" /> Upload</span>
                      <Plus className="h-4 w-4 text-amber-400" />
                    </button>
                  ))}
                  {recordings.map((r) => (
                    <button key={`r-${r.id}`} data-testid={`add-rec-${r.id}`} onClick={() => addItem("recording", r)} className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 text-left">
                      <div className="h-12 w-20 rounded bg-black overflow-hidden flex items-center justify-center shrink-0">{r.thumbnail_url ? <img src={r.thumbnail_url} className="h-full w-full object-cover" alt="" /> : <Film className="h-4 w-4 text-gray-600" />}</div>
                      <span className="text-sm text-gray-200 truncate flex-1">{r.title}</span>
                      <span className="text-[10px] text-gray-500 inline-flex items-center gap-1"><Radio className="h-3 w-3" /> Broadcast</span>
                      <Plus className="h-4 w-4 text-amber-400" />
                    </button>
                  ))}
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Embed modal */}
        <Dialog open={embedOpen} onOpenChange={setEmbedOpen}>
          <DialogContent className="bg-card border-gray-800 max-w-lg">
            <DialogHeader><DialogTitle className="font-display text-lg flex items-center gap-2"><Code2 className="h-5 w-5 text-amber-400" /> Embed "{active.name}"</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-400">Paste this on your website to show just this playlist.</p>
              <label className="flex items-center justify-between rounded-xl border border-gray-800 bg-gray-950 px-4 py-3 cursor-pointer">
                <span>
                  <span className="text-sm text-gray-200 block">Hide top bar (video only)</span>
                  <span className="text-[11px] text-gray-500">Removes the branding bar so only the player + playlist show.</span>
                </span>
                <input data-testid="embed-hide-bar-toggle" type="checkbox" checked={hideBar} onChange={(e) => setHideBar(e.target.checked)} className="h-4 w-4 accent-amber-500 cursor-pointer" />
              </label>
              {!hideBar && (
                <div className="space-y-2">
                  <label className="flex items-center justify-between rounded-xl border border-gray-800 bg-gray-950 px-4 py-3 cursor-pointer">
                    <span>
                      <span className="text-sm text-gray-200 block">Match bar colors to my website</span>
                      <span className="text-[11px] text-gray-500">Override the default branding color on the top bar.</span>
                    </span>
                    <input data-testid="embed-custom-colors-toggle" type="checkbox" checked={customColors} onChange={(e) => setCustomColors(e.target.checked)} className="h-4 w-4 accent-amber-500 cursor-pointer" />
                  </label>
                  {customColors && (
                    <div className="flex flex-wrap items-center gap-5 rounded-xl border border-gray-800 bg-gray-950 p-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">Bar color</span>
                        <input data-testid="embed-bar-color" type="color" value={barColor} onChange={(e) => setBarColor(e.target.value)} className="h-9 w-12 rounded-lg bg-transparent border border-gray-800 cursor-pointer" />
                        <span className="text-xs text-gray-500 font-mono">{barColor}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">Text</span>
                        <input data-testid="embed-bar-text-color" type="color" value={barTextColor} onChange={(e) => setBarTextColor(e.target.value)} className="h-9 w-12 rounded-lg bg-transparent border border-gray-800 cursor-pointer" />
                        <span className="text-xs text-gray-500 font-mono">{barTextColor}</span>
                      </div>
                      <div className="rounded-md px-3 py-1.5 text-xs font-semibold" style={{ background: barColor, color: barTextColor }} data-testid="embed-bar-preview">{active.name}</div>
                    </div>
                  )}
                </div>
              )}
              <div className="rounded-xl border border-gray-800 bg-gray-950 p-3"><code data-testid="playlist-embed-code" className="block text-xs text-gray-300 break-all font-mono whitespace-pre-wrap">{embedCode}</code></div>
              <div className="flex items-center justify-between">
                <a href={embedSrc} target="_blank" rel="noreferrer" className="text-sm text-amber-400 hover:underline">Preview →</a>
                <button data-testid="copy-playlist-embed" onClick={copyEmbed} className="gold-gradient text-black font-semibold px-4 py-2 rounded-lg flex items-center gap-2">{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : "Copy code"}</button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ---------- Playlist list ----------
  return (
    <div className="p-6 lg:p-8 max-w-[1200px] mx-auto">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Playlists</h1>
          <p className="text-gray-400 mt-1.5 text-sm">Group your broadcasts and uploaded videos into playlists you can embed on your website.</p>
        </div>
        <button data-testid="new-playlist-btn" onClick={() => setCreating(true)} className="gold-gradient text-black font-semibold px-4 py-2.5 rounded-lg flex items-center gap-2 hover:brightness-110"><Plus className="h-4 w-4" /> New playlist</button>
      </div>

      {creating && (
        <div className="mb-6 flex gap-2 max-w-md">
          <Input data-testid="new-playlist-name" autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} placeholder="e.g. Sunday Services" className="bg-gray-950 border-gray-800" />
          <button data-testid="create-playlist-confirm" onClick={create} className="gold-gradient text-black font-semibold px-4 rounded-lg">Create</button>
          <button onClick={() => { setCreating(false); setNewName(""); }} className="px-3 text-gray-400 hover:text-white">Cancel</button>
        </div>
      )}

      {playlists.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-gray-800 rounded-2xl">
          <div className="h-14 w-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto"><ListVideo className="h-7 w-7 text-amber-400" /></div>
          <p className="mt-4 text-white font-medium">No playlists yet</p>
          <p className="text-gray-500 text-sm mt-1">Create one like "Sunday Services" to start organizing your videos.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {playlists.map((pl, i) => (
            <motion.div key={pl.id} data-testid={`playlist-card-${pl.id}`} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.05, 0.3) }}
              className="group bg-card border border-gray-800 hover:border-amber-500/40 rounded-2xl p-5 transition-all">
              <div className="flex items-start justify-between">
                <div className="h-11 w-11 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center"><ListVideo className="h-5 w-5 text-amber-400" /></div>
                <div className="flex items-center gap-1">
                  <button
                    data-testid={`toggle-default-${pl.id}`}
                    onClick={() => toggleDefault(pl)}
                    title={pl.is_default ? "This is your default playlist — new videos auto-add here. Click to unset." : "Set as default — new uploads & recordings auto-add here"}
                    className={"h-8 w-8 rounded-lg flex items-center justify-center transition-colors " + (pl.is_default ? "text-amber-400 hover:bg-amber-500/10" : "text-gray-600 hover:text-amber-400 hover:bg-amber-500/10 opacity-0 group-hover:opacity-100")}>
                    <Star className={"h-4 w-4 " + (pl.is_default ? "fill-amber-400" : "")} />
                  </button>
                  <button data-testid={`delete-playlist-${pl.id}`} onClick={() => removePlaylist(pl.id)} className="h-8 w-8 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <button onClick={() => openPlaylist(pl.id)} className="mt-4 text-left w-full">
                <p className="font-semibold text-white flex items-center gap-2">
                  {pl.name}
                  {pl.is_default && <span data-testid={`default-badge-${pl.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40"><Star className="h-2.5 w-2.5 fill-amber-300" /> DEFAULT</span>}
                  {pl.auto_stream_id && <span data-testid={`church-badge-${pl.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300 border border-sky-500/30"><Radio className="h-2.5 w-2.5" /> CHANNEL</span>}
                </p>
                <p className="text-xs text-gray-500 mt-1">{pl.item_count} video{pl.item_count === 1 ? "" : "s"}</p>
              </button>
              <button onClick={() => openPlaylist(pl.id)} className="mt-4 w-full text-sm text-amber-400 hover:underline text-left">Open playlist →</button>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
